import { ApplicationState } from "../store";
import { KnownAction } from "../store/actions/GalleryActions";
import { GalleryElement } from "../store/Gallery";

export class GalleryService {
  private dispatch: (action: KnownAction) => void;
  private state: ApplicationState;
  private url =
    process.env.NODE_ENV === "development" ? "https://localhost:44314/" : "";

  constructor(
    dispatch: (action: KnownAction) => void,
    state: ApplicationState
  ) {
    this.dispatch = dispatch;
    this.state = state;
  }

  public async getGalleryElements() {
    const requestOptions: RequestInit = {
      method: "GET",
    };

    await fetch(`${this.url}api/galleryelements`, requestOptions)
      .then(
        async (response) => (await response.json()) as Promise<GalleryElement[]>
      )
      .then((data) => {
        this.dispatch({
          type: "RECEIVE_GALLERY_ELEMENTS",
          galleryElements: data,
        });
      });
  }

  public async getGalleryElement(id: number) {
    const requestOptions: RequestInit = {
      method: "GET",
      headers: this.getHeaderWithAuthorization(),
    };
    return await fetch(
      `${this.url}api/galleryelements/${id}`,
      requestOptions
    ).then(async (response) => {
      const result = await (response.json() as Promise<App.Result>);
      if (result.resultType === 1) {
        this.dispatch({
          type: "RECEIVE_GET_GALLERY_ELEMENT",
          galleryElement: result.value as GalleryElement,
          succeed: true,
        });
      }
    });
  }

  public async getGalleryElementForEdit(id: number) {
    const requestOptions: RequestInit = {
      method: "GET",
      headers: this.getHeaderWithAuthorization(),
    };
    return await fetch(
      `${this.url}api/galleryelements/${id}`,
      requestOptions
    ).then(async (response) => {
      const result = await (response.json() as Promise<App.Result>);
      if (result.resultType === 1) {
        this.dispatch({
          type: "RECEIVE_GET_GALLERY_ELEMENT_FOR_EDIT",
          galleryElement: result.value as GalleryElement,
          succeed: true,
        });
      }
    });
  }

  public async getGalleryElementsByParentId(parentId: number) {
    const requestOptions: RequestInit = {
      method: "GET",
      headers: this.getHeaderWithAuthorization(),
    };
    return await fetch(
      `${this.url}api/galleryelements/ByParentId/${parentId}`,
      requestOptions
    ).then(async (response) => {
      if (response.status === 401) {
        this.dispatch({
          type: "UNAUTHORIZED",
        });
        return;
      }
      const result = await (response.json() as Promise<App.Result>);
      if (result.resultType === 1) {
        this.dispatch({
          type: "RECEIVE_GALLERY_ELEMENTS_BY_PARENTID",
          galleryElementsByParentId: result.value as GalleryElement[],
          succeed: true,
        });
      }
    });
  }

  public async createGalleryElement(element: GalleryElement) {
    const requestOptions: RequestInit = {
      method: "POST",
      headers: this.getHeaderWithAuthorization(),
      body: JSON.stringify(element),
    };
    return await fetch(`${this.url}api/galleryelements`, requestOptions).then(
      async (response) => {
        const result = await (response.json() as Promise<App.Result>);
        if (result.resultType === 1) {
          this.dispatch({
            type: "RECEIVE_CREATE_GALLERY_ELEMENT",
            galleryElements: result.value as GalleryElement[],
            succeed: true,
          });
        }
      }
    );
  }

  public async updateGalleryElement(element: GalleryElement) {
    const requestOptions: RequestInit = {
      method: "PUT",
      headers: this.getHeaderWithAuthorization(),
      body: JSON.stringify(element),
    };
    return await fetch(
      `${this.url}api/galleryelements/${element.id}`,
      requestOptions
    ).then(async (response) => {
      const result = await (response.json() as Promise<App.Result>);
      // TODO: find out why is not possible to use enum here
      if (result.resultType === 1) {
        this.dispatch({
          type: "RECEIVE_UPDATE_GALLERY_ELEMENT",
          galleryElement: result.value as GalleryElement,
          succeed: true,
        });
      }
    });
  }

  public async deleteGalleryElement(elementId: number) {
    const requestOptions: RequestInit = {
      method: "DELETE",
      headers: this.getHeaderWithAuthorization(),
    };
    return await fetch(
      `${this.url}api/galleryelements/${elementId}`,
      requestOptions
    ).then(async (response) => {
      const result = await (response.json() as Promise<App.Result>);
      if (result.resultType === 1) {
        this.dispatch({
          type: "RECEIVE_DELETE_GALLERY_ELEMENT",
          galleryElement: result.value as GalleryElement,
          succeed: true,
        });
      }
    });
  }

  public async uploadImage(formFile: File) {
    const formData = new FormData();
    formData.append("formFile", formFile);

    return await fetch(`${this.url}api/images`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.state.user?.user.token}`,
      },
      body: formData,
    }).then(async (response) => {
      const result = await (response.json() as Promise<App.Result>);
      if (result.resultType === 1) {
        this.dispatch({
          type: "RECEIVE_UPLOAD_IMAGES",
          uploadedImageUrl: result.value as unknown as string,
          succeed: true,
        });
      }
    });
  }

  public async getImages() {
    const requestOptions: RequestInit = {
      method: "GET",
      headers: this.getHeaderWithAuthorization(),
    };

    return await fetch(`${this.url}api/images`, requestOptions).then(
      async (response) => {
        const result = await (response.json() as Promise<App.Result>);
        if (result.resultType === 1) {
          this.dispatch({
            type: "RECEIVE_IMAGES",
            images: result.value as string[],
            succeed: true,
          });
        }
      }
    );
  }

  public async deleteImages(images: string[]) {
    const requestOptions: RequestInit = {
      method: "DELETE",
      headers: this.getHeaderWithAuthorization(),
      body: JSON.stringify(images),
    };

    return await fetch(`${this.url}api/images`, requestOptions).then(
      async (response) => {
        const result = await (response.json() as Promise<App.Result>);
        if (result.resultType === 1) {
          this.dispatch({
            type: "DELETE_IMAGES",
            images: images,
            succeed: true,
          });
        }
      }
    );
  }

  private getHeaderWithAuthorization(): HeadersInit {
    return {
      Authorization: `Bearer ${this.state.user?.user.token}`,
      "Content-Type": "application/json",
    };
  }
}
